// Authenticated API client used throughout the app.
//
// Wraps fetch with:
//   - JWT bearer token from localStorage
//   - JSON Content-Type by default (auto-removed for FormData)
//   - 401 handler that clears auth and reloads to send the user to /login
//   - Returns null on non-2xx instead of throwing
//
// All API calls in App.tsx and downstream component files should go
// through this so auth handling stays in one place.

export const apiCall = async (path: string, options: RequestInit = {}): Promise<any> => {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(options.headers as Record<string, string> | undefined) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // X-Client-Context: optional per-session narrowing of req.clientScope on the
  // backend. Sourced from sessionStorage, written by ClientScopeContext when
  // the user picks a client. Absent or "all" → backend uses full user scope.
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all" && !headers["X-Client-Context"]) {
      headers["X-Client-Context"] = ctx;
    }
  } catch { /* sessionStorage unavailable — skip silently */ }
  if (options.body instanceof FormData) delete headers["Content-Type"];
  try {
    const res = await fetch(`/api${path}`, { ...options, headers });
    if (res.status === 401) {
      localStorage.removeItem("kontractai_token");
      localStorage.removeItem("kontractai_user");
      window.location.reload();
      return null;
    }
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
};

// ── Authenticated document access (AUTHDOCSERVE_20260625) ────────────────────
// The open `/uploads/*` static mount was removed; documents are served only by
// the authenticated by-ID endpoints in backend routes/documents.js. Auth is
// Bearer-only, so browser-native opens (window.open / <a href> / ms-word) cannot
// attach the header. We therefore fetch the bytes WITH the bearer token and turn
// them into a Blob URL for view/download; the one exception is "Open in Word",
// where Word fetches the URL itself, so we mint a short-TTL signed token.

export type DocType = "version" | "executed" | "template";

async function fetchDocBlob(dtype: DocType, id: string | number, disposition: "inline" | "attachment"): Promise<Blob | null> {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all") headers["X-Client-Context"] = ctx;
  } catch { /* ignore */ }
  try {
    const res = await fetch(`/api/documents/file?dtype=${dtype}&id=${encodeURIComponent(String(id))}&disposition=${disposition}`, { headers });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

// Open a document inline in a new tab (PDF view). Opens the tab synchronously
// (pre-fetch) to avoid popup blocking, then points it at the fetched Blob URL.
export const viewDocument = async (dtype: DocType, id: string | number): Promise<boolean> => {
  const win = window.open("", "_blank");
  const blob = await fetchDocBlob(dtype, id, "inline");
  if (!blob) { if (win) win.close(); alert("Unable to open document (not found or access denied)."); return false; }
  const url = URL.createObjectURL(blob);
  if (win) win.location.href = url; else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Download a document (attachment) by id.
export const downloadDocument = async (dtype: DocType, id: string | number, fileName?: string): Promise<boolean> => {
  const blob = await fetchDocBlob(dtype, id, "attachment");
  if (!blob) { alert("Unable to download document (not found or access denied)."); return false; }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fileName || "document";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Returns a Blob for callers that need the raw bytes (e.g. attaching a PDF to AI chat).
export const getDocumentBlob = (dtype: DocType, id: string | number): Promise<Blob | null> =>
  fetchDocBlob(dtype, id, "inline");

// Open a docx in the desktop Word app. Word fetches the URL itself (no bearer),
// so mint a short-TTL signed token first via the authenticated mint endpoint.
export const openInWord = async (dtype: DocType, id: string | number): Promise<boolean> => {
  const res = await apiCallEx(`/documents/download-token`, { method: "POST", body: JSON.stringify({ dtype, id, disposition: "inline" }) });
  if (!res.ok || !res.data?.token) { alert("Unable to open in Word (not found or access denied)."); return false; }
  window.location.href = `ms-word:ofe|u|${window.location.origin}/api/documents/open?dt=${encodeURIComponent(res.data.token)}`;
  return true;
};

// Variant of apiCall that EXPOSES the HTTP status and error body instead of
// collapsing every non-2xx to null. Use this where the caller must distinguish
// outcomes — e.g. the platform tenant-create flow needs to show the friendly
// "admin email already in use" message on a 409 rather than a generic failure.
// Same auth/401 handling as apiCall; never throws.
export interface ApiResult { ok: boolean; status: number; data: any }
export const apiCallEx = async (path: string, options: RequestInit = {}): Promise<ApiResult> => {
  const token = localStorage.getItem("kontractai_token");
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(options.headers as Record<string, string> | undefined) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try {
    const ctx = sessionStorage.getItem("kontractai_client_scope");
    if (ctx && ctx !== "all" && !headers["X-Client-Context"]) headers["X-Client-Context"] = ctx;
  } catch { /* sessionStorage unavailable — skip silently */ }
  if (options.body instanceof FormData) delete headers["Content-Type"];
  try {
    const res = await fetch(`/api${path}`, { ...options, headers });
    if (res.status === 401) {
      localStorage.removeItem("kontractai_token");
      localStorage.removeItem("kontractai_user");
      window.location.reload();
      return { ok: false, status: 401, data: null };
    }
    let data: any = null;
    try { data = await res.json(); } catch { /* empty/non-JSON body */ }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
};
